import { createAction, props } from '@ngrx/store';
import { IPageCount } from 'src/app/shared/models/page.model';
import { APIResponse } from 'src/app/core/models/api-response';
import { IAddRole, ICustomField, ICustomFieldForm, IModule, INotification, INotificationTypeUpdate, IObjectCustomFields, IObjectCustomFieldsParams, IRole, IUser, IOrdersSettings, IUserCreate, IUpdateRole, ISplittingRule, ISplittingRuleCreate, IAddTeam, ITeam, ITeamOption, ITeamSort, IUpdateTeam } from '../models/settings.models';

// Get Me
export const GetMe = createAction('[ Settings ] - GetMe');
export const GetMeSuccess = createAction(
  '[ Settings ] - GetMe Success ',
  props<{ user: IUser }>()
);
export const GetMeError = createAction('[ Settings ] - GetMe Error');

// Update Me
export const UpdateMe = createAction('[ Settings ] - UpdateMe', props<{ user: IUser }>());
export const UpdateMeSuccess = createAction(
  '[ Settings ] - UpdateMe Success ',
  props<{ user: IUser }>()
);
export const UpdateMeError = createAction('[ Settings ] - UpdateMe Error');

// fetch all users
export const FetchAllUsers = createAction(
  '[ Users ] - FetchAllUsers',
  props<{ enabled?: 'true' | 'false' | 'all'; deleted?: 'true' | 'false' | 'all' }>()
);
export const FetchAllUsersSuccess = createAction(
  '[ Users ] - FetchAllUsers Success ',
  props<{ users: IUser[] }>()
);
export const FetchAllUsersError = createAction(
  '[ Users ] - FetchAllUser Error'
);

// fetch users
export const FetchUsers = createAction(
  '[ Users ] - FetchUsers',
  props<{ page: IPageCount; enabled?: 'true' | 'false' | 'all'; deleted?: 'true' | 'false' | 'all'; search?: string }>()
);
export const FetchUsersSuccess = createAction(
  '[ Users ] - FetchUsers Success ',
  props<{ users: APIResponse<IUser> }>()
);
export const FetchUsersError = createAction('[ Users ] - FetchUser Error');

// add user
export const AddUser = createAction(
  '[ User ] - AddUser',
  props<{ user: IUserCreate }>()
);
export const AddUserSuccess = createAction(
  '[ User ] - AddUser Success ',
  props<{ user: IUser }>()
);
export const AddUserError = createAction('[ User ] - AddUser Error');

// Update user
export const UpdateUser = createAction(
  '[ User ] - UpdateUser',
  props<{ id: number; user: Partial<IUser> }>()
);
export const UpdateUserSuccess = createAction(
  '[ User ] - UpdateUser Success ',
  props<{ user: IUser }>()
);
export const UpdateUserError = createAction('[ User ] - UpdateUser Error');

// Delete user (soft delete)
export const DeleteUser = createAction(
  '[ User ] - DeleteUser',
  props<{ id: number }>()
);
export const DeleteUserSuccess = createAction('[ User ] - DeleteUser Success ');
export const DeleteUserError = createAction('[ User ] - DeleteUser Error');

// Restore soft-deleted user
export const RestoreUser = createAction(
  '[ User ] - RestoreUser',
  props<{ id: number }>()
);
export const RestoreUserSuccess = createAction(
  '[ User ] - RestoreUser Success',
  props<{ user: IUser }>()
);
export const RestoreUserError = createAction('[ User ] - RestoreUser Error');

// fetch all roles
export const FetchAllRoles = createAction('[ Roles ] - FetchAllRoles');
export const FetchAllRolesSuccess = createAction(
  '[ Roles ] - FetchAllRoles Success ',
  props<{ roles: IRole[] }>()
);
export const FetchAllRolesError = createAction(
  '[ Roles ] - FetchAllRole Error'
);

// fetch roles
export const FetchRoles = createAction(
  '[ Roles ] - FetchRoles',
  props<{ page: IPageCount }>()
);
export const FetchRolesSuccess = createAction(
  '[ Roles ] - FetchRoles Success ',
  props<{ roles: APIResponse<IRole> }>()
);
export const FetchRolesError = createAction('[ Roles ] - FetchRole Error');

// add role
export const AddRole = createAction(
  '[ Role ] - AddRole',
  props<{ role: IAddRole }>()
);
export const AddRoleSuccess = createAction(
  '[ Role ] - AddRole Success ',
  props<{ role: IRole }>()
);
export const AddRoleError = createAction('[ Role ] - AddRole Error');

// Update role
export const UpdateRole = createAction(
  '[ Role ] - UpdateRole',
  props<{ id: number; role: IUpdateRole }>()
);
export const UpdateRoleSuccess = createAction(
  '[ Role ] - UpdateRole Success ',
  props<{ role: IRole }>()
);
export const UpdateRoleError = createAction('[ Role ] - UpdateRole Error');

// Delete role
export const DeleteRole = createAction(
  '[ Role ] - DeleteRole',
  props<{ id: number }>()
);
export const DeleteRoleSuccess = createAction('[ Role ] - DeleteRole Success ');
export const DeleteRoleError = createAction('[ Role ] - DeleteRole Error');

// fetch all teams
// Carries the sort because `GET /v1/teams` sorts server-side; omitting it leaves the
// backend default of `name asc`.
export const FetchAllTeams = createAction(
  '[ Teams ] - FetchAllTeams',
  props<{ sort?: ITeamSort }>()
);
export const FetchAllTeamsSuccess = createAction(
  '[ Teams ] - FetchAllTeams Success ',
  props<{ teams: ITeam[] }>()
);
export const FetchAllTeamsError = createAction(
  '[ Teams ] - FetchAllTeams Error'
);

// fetch the team options for filter pickers
// A separate action from `FetchAllTeams`, not a variant of it: that one reads the
// permission-gated `GET /v1/teams` and lands in `allTeams` with members attached. This one
// reads `GET /v1/teams/options`, is open to ordinary order/inbox users, and lands in its
// own `teamOptions` slice so a 403-gated payload and a public one never share a key.
export const FetchTeamOptions = createAction('[ Teams ] - FetchTeamOptions');
export const FetchTeamOptionsSuccess = createAction(
  '[ Teams ] - FetchTeamOptions Success ',
  props<{ teams: ITeamOption[] }>()
);
export const FetchTeamOptionsError = createAction(
  '[ Teams ] - FetchTeamOptions Error'
);

// add team
export const AddTeam = createAction(
  '[ Team ] - AddTeam',
  props<{ team: IAddTeam }>()
);
export const AddTeamSuccess = createAction(
  '[ Team ] - AddTeam Success ',
  props<{ team: ITeam }>()
);
export const AddTeamError = createAction('[ Team ] - AddTeam Error');

// Update team
export const UpdateTeam = createAction(
  '[ Team ] - UpdateTeam',
  props<{ id: number; team: IUpdateTeam }>()
);
export const UpdateTeamSuccess = createAction(
  '[ Team ] - UpdateTeam Success ',
  props<{ team: ITeam }>()
);
export const UpdateTeamError = createAction('[ Team ] - UpdateTeam Error');

// Delete team
export const DeleteTeam = createAction(
  '[ Team ] - DeleteTeam',
  props<{ id: number }>()
);
export const DeleteTeamSuccess = createAction('[ Team ] - DeleteTeam Success ');
export const DeleteTeamError = createAction('[ Team ] - DeleteTeam Error');

// `SetTeamsLoading`, not `SetLoading`: `SetRolesLoader` is `'[ Roles ] - SetLoading'` and is
// registered twice in the reducer. A distinct type string keeps Teams out of that.
export const SetTeamsLoader = createAction(
  '[ Teams ] - SetTeamsLoading',
  props<{ isLoading: boolean }>()
);

// fetch all modules
export const FetchAllModules = createAction('[ Modules ] - FetchAllModules');
export const FetchAllModulesSuccess = createAction(
  '[ Modules ] - FetchAllModules Success ',
  props<{ modules: IModule[] }>()
);
export const FetchAllModulesError = createAction(
  '[ Modules ] - FetchAllModule Error'
);

// looder actions
export const SetUsersLoader = createAction(
  '[ Users ] - SetLoading',
  props<{ isLoading: boolean }>()
);
export const SetRolesLoader = createAction(
  '[ Roles ] - SetLoading',
  props<{ isLoading: boolean }>()
);
export const SetModulesLoader = createAction(
  '[ Modules ] - SetLoading',
  props<{ isLoading: boolean }>()
);
export const SetNotificationsLoader = createAction(
  '[ Notifications ] - SetLoading',
  props<{ isLoading: boolean }>()
)

// Language Actions
export const setLanguage = createAction(
  '[Settings] Set Language',
  props<{ lang: string }>()
);
export const setLanguageSuccess = createAction(
  '[Settings] Set Language Success',
  props<{ lang: string }>()
);
export const setLanguageError = createAction(
  '[Settings] Set Language Error',
  props<{ error: any }>()
);

// Get Notifications Actions
export const getNotifications = createAction(
  '[Settings] Set Notification',
);
export const getNotificationsSuccess = createAction(
  '[Settings] Set Notification Success',
  props<{ notifications: INotification[] }>()
);
export const getNotificationsError = createAction(
  '[Settings] Set Notification Error'
);

// Update Notifications Actions
export const updateNotifications = createAction(
  '[Settings] Update Notification',
  props<{ id: number; notificationType: INotificationTypeUpdate }>()
);
export const updateNotificationsSuccess = createAction(
  '[Settings] Update Notification Success',
  props<{ notificationType: INotificationTypeUpdate }>()
);
export const updateNotificationsError = createAction(
  '[Settings] Update Notification Error'
);

// Custom Fields Actions
export const FetchAllCustomFields = createAction('[ Custom Fields ] - FetchAllCustomFields');
export const FetchAllCustomFieldsSuccess = createAction(
  '[ Custom Fields ] - FetchAllCustomFields Success ',
  props<{ customFields: APIResponse<ICustomField> }>()
);
export const FetchAllCustomFieldsError = createAction(
  '[ Custom Fields ] - FetchAllCustomField Error'
);

// Paginated Custom Fields (infinite scroll)
export const FetchCustomFieldsPage = createAction(
  '[ Custom Fields ] - FetchCustomFieldsPage',
  props<{ page?: IPageCount; append: boolean, object_type?: string }>()
);
export const FetchCustomFieldsPageSuccess = createAction(
  '[ Custom Fields ] - FetchCustomFieldsPage Success ',
  props<{ customFields: APIResponse<ICustomField>; append: boolean }>()
);
export const FetchCustomFieldsPageError = createAction(
  '[ Custom Fields ] - FetchCustomFieldsPage Error'
);

export const AddCustomField = createAction('[ Custom Fields ] - AddCustomField', props<{ customField: ICustomFieldForm }>());
export const AddCustomFieldSuccess = createAction(
  '[ Custom Fields ] - AddCustomField Success ',
  props<{ customField: ICustomField }>()
);
export const AddCustomFieldError = createAction('[ Custom Fields ] - AddCustomField Error');

export const UpdateCustomField = createAction('[ Custom Fields ] - UpdateCustomField', props<{ id: number; customField: ICustomFieldForm }>());
export const UpdateCustomFieldSuccess = createAction(
  '[ Custom Fields ] - UpdateCustomField Success ',
  props<{ customField: ICustomField }>()
);
export const UpdateCustomFieldError = createAction('[ Custom Fields ] - UpdateCustomField Error');

export const DeleteCustomField = createAction('[ Custom Fields ] - DeleteCustomField', props<{ id: number }>());
export const DeleteCustomFieldSuccess = createAction('[ Custom Fields ] - DeleteCustomField Success ', props<{ id: number }>());
export const DeleteCustomFieldError = createAction('[ Custom Fields ] - DeleteCustomField Error');

// Get Object Custom Fields
export const GetObjectCustomFields = createAction('[ Object Custom Fields ] - GetObjectCustomFields', props<{ params: IObjectCustomFieldsParams }>());
export const GetObjectCustomFieldsSuccess = createAction(
  '[ Object Custom Fields ] - GetObjectCustomFields Success ',
  props<{ objectCustomFields: IObjectCustomFields }>()
);
export const GetObjectCustomFieldsError = createAction('[ Object Custom Fields ] - GetObjectCustomFields Error');

// Update Object Custom Fields values
export const UpdateObjectCustomFields = createAction(
  '[ Object Custom Fields ] - UpdateObjectCustomFields',
  props<{ params: IObjectCustomFieldsParams; values: IObjectCustomFields['values'] }>()
);
export const UpdateObjectCustomFieldsSuccess = createAction(
  '[ Object Custom Fields ] - UpdateObjectCustomFields Success ',
  props<{ objectCustomFields: IObjectCustomFields }>()
);
export const UpdateObjectCustomFieldsError = createAction('[ Object Custom Fields ] - UpdateObjectCustomFields Error');

// Custom Fields Loader Actions
export const SetCustomFieldsLoader = createAction(
  '[ Custom Fields ] - SetLoading',
  props<{ isLoading: boolean }>()
);

// Orders Settings Actions
export const GetOrdersSettings = createAction('[ Orders Settings ] - GetOrdersSettings');
export const GetOrdersSettingsSuccess = createAction(
  '[ Orders Settings ] - GetOrdersSettings Success',
  props<{ settings: IOrdersSettings }>()
);
export const GetOrdersSettingsError = createAction('[ Orders Settings ] - GetOrdersSettings Error');

export const UpdateOrdersSettings = createAction(
  '[ Orders Settings ] - UpdateOrdersSettings',
  props<{ settings: IOrdersSettings }>()
);
export const UpdateOrdersSettingsSuccess = createAction(
  '[ Orders Settings ] - UpdateOrdersSettings Success',
  props<{ settings: IOrdersSettings }>()
);
export const UpdateOrdersSettingsError = createAction('[ Orders Settings ] - UpdateOrdersSettings Error');

// Splitting Rules Actions
export const LoadSplittingRules = createAction('[ Splitting Rules ] - Load');
export const LoadSplittingRulesSuccess = createAction(
  '[ Splitting Rules ] - Load Success',
  props<{ rules: ISplittingRule[] }>()
);
export const LoadSplittingRulesError = createAction('[ Splitting Rules ] - Load Error');

export const CreateSplittingRule = createAction(
  '[ Splitting Rules ] - Create',
  props<{ rule: ISplittingRuleCreate }>()
);
export const CreateSplittingRuleSuccess = createAction(
  '[ Splitting Rules ] - Create Success',
  props<{ rule: ISplittingRule }>()
);
export const CreateSplittingRuleError = createAction('[ Splitting Rules ] - Create Error');

export const UpdateSplittingRule = createAction(
  '[ Splitting Rules ] - Update',
  props<{ id: number; rule: Partial<ISplittingRuleCreate> }>()
);
export const UpdateSplittingRuleSuccess = createAction(
  '[ Splitting Rules ] - Update Success',
  props<{ rule: ISplittingRule }>()
);
export const UpdateSplittingRuleError = createAction('[ Splitting Rules ] - Update Error');

export const DeleteSplittingRule = createAction(
  '[ Splitting Rules ] - Delete',
  props<{ id: number }>()
);
export const DeleteSplittingRuleSuccess = createAction(
  '[ Splitting Rules ] - Delete Success',
  props<{ id: number }>()
);
export const DeleteSplittingRuleError = createAction('[ Splitting Rules ] - Delete Error');